<template>
  <div class="custom-block">
    <div v-if="layout.controls?.length" class="custom-block-controls">
      <label
        v-for="control in layout.controls"
        :key="control.name"
        class="custom-block-control"
      >
        <span>{{ control.label }}</span>
        <v-select
          class="custom-block-select"
          :model-value="selected[control.name] ?? ''"
          :options="selectOptions(control)"
          :reduce="(option) => option.value"
          label="label"
          :clearable="false"
          :searchable="selectOptions(control).length > 8"
          :disabled="!!preview"
          @update:modelValue="choose(control.name, $event)"
        />
      </label>
    </div>

    <EmptyState
      v-if="failed"
      icon="!"
      :title="$t('dashboardBlocks.loadFailed')"
      :text="failed"
    />
    <div v-else class="custom-block-grid" :class="{ loading }">
      <div
        v-for="(widget, i) in layout.widgets"
        :key="i"
        class="custom-block-widget"
        :style="widgetStyle(widget)"
      >
        <div v-if="widget.type === 'kpi'" class="custom-block-kpi">
          <span class="custom-block-label">{{ widget.label }}</span>
          <b
            class="bold-24"
            :class="styleClasses(widget.style, kpiValue(widget))"
            >{{ format(kpiValue(widget), widget.format) }}</b
          >
        </div>

        <template v-else-if="widget.type === 'chart'">
          <span v-if="widget.label" class="custom-block-label">{{
            widget.label
          }}</span>
          <div class="custom-block-chart">
            <component
              :is="chartComponent(widget.kind)"
              :chartData="chartData(widget)"
              :options="chartOptions(widget)"
              :styles="{ width: '100%', height: '100%' }"
            />
          </div>
        </template>

        <template v-else-if="widget.type === 'table'">
          <span v-if="widget.label" class="custom-block-label">{{
            widget.label
          }}</span>
          <div class="custom-block-table">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th
                    v-for="column in widget.columns"
                    :key="column.field"
                    :class="alignClass(column)"
                  >
                    {{ column.label || column.field }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, r) in rowsOf(widget)" :key="r">
                  <td
                    v-for="column in widget.columns"
                    :key="column.field"
                    :class="[
                      alignClass(column),
                      styleClasses(column.style, row[column.field]),
                    ]"
                  >
                    {{ format(row[column.field], column.format) }}
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="!rowsOf(widget).length" class="text-muted regular-14 p-2">
              {{ $t("dashboardBlocks.noData") }}
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import { LineChart, BarChart, PieChart, DoughnutChart } from "vue-chart-3";
import http from "../../modules/http";
import EmptyState from "../EmptyState.vue";

const PALETTE = [
  "#713AE8",
  "#1C8C5E",
  "#E8803A",
  "#2B7BD9",
  "#C2417A",
  "#7A7A8C",
];

export default {
  name: "CustomBlock",
  components: { EmptyState, LineChart, BarChart, PieChart, DoughnutChart },
  emits: ["loaded"],
  props: {
    modelValue: Object,
    appId: String,
    preview: Object,
  },
  data: function () {
    return {
      store: useStore(),
      data: {},
      options: {},
      selected: {},
      currency: null,
      loading: false,
      failed: "",
      requestId: 0,
    };
  },
  computed: {
    block() {
      return this.modelValue?.block || {};
    },
    layout() {
      return this.block.layout || { widgets: [], controls: [] };
    },
    uses() {
      return this.layout.uses || [];
    },
    date() {
      return this.store.state.topBar.date;
    },
    warehouse() {
      return this.store.state.topBar.warehouse;
    },
  },
  watch: {
    date: {
      deep: true,
      handler() {
        if (this.uses.includes("date")) this.load();
      },
    },
    warehouse() {
      if (this.uses.includes("warehouse")) this.load();
    },
    preview() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    selectOptions(control) {
      const options = this.options[control.name] || [];
      return control.all
        ? [{ value: "", label: this.$t("dashboardBlocks.all") }, ...options]
        : options;
    },
    choose(name, value) {
      this.selected = { ...this.selected, [name]: value ?? "" };
      this.load();
    },
    day(value) {
      return value instanceof Date ? value.toISOString().split("T")[0] : value;
    },
    load() {
      if (this.preview) {
        this.data = this.preview.data;
        this.options = this.preview.options;
        this.currency = this.preview.currency;
        return;
      }
      const query = new URLSearchParams();
      if (this.uses.includes("date") && this.date?.[0]) {
        query.set("from", this.day(this.date[0]));
        if (this.date[1]) query.set("to", this.day(this.date[1]));
      }
      if (this.uses.includes("warehouse") && this.warehouse) {
        query.set("warehouse", this.warehouse);
      }
      for (const name in this.selected) {
        if (this.selected[name] !== "") {
          query.set(`controls[${name}]`, this.selected[name]);
        }
      }

      const request = ++this.requestId;
      this.loading = true;
      http
        .fetch(
          `/dashboard/custom-blocks/${this.block.id}/data?${query.toString()}`,
          null,
          false,
          null,
          null,
          true,
          false
        )
        .then((result) => {
          if (request !== this.requestId) return;
          this.data = result.data;
          this.options = result.options;
          this.currency = result.currency;
          this.failed = "";
        })
        .catch((error) => {
          if (request !== this.requestId) return;
          this.failed = error?.message || "";
          if (!this.failed) this.failed = this.$t("dashboardBlocks.tryAgain");
        })
        .finally(() => {
          if (request !== this.requestId) return;
          this.loading = false;
          this.$emit("loaded");
        });
    },
    widgetStyle(widget) {
      return {
        gridColumn: `${widget.x + 1} / span ${widget.w}`,
        gridRow: `${widget.y + 1} / span ${widget.h}`,
      };
    },
    rowsOf(widget) {
      return this.data[widget.query] || [];
    },
    kpiValue(widget) {
      return this.rowsOf(widget)[0]?.[widget.field];
    },
    alignClass(column) {
      if (column.style?.align) {
        return {
          left: "text-start",
          center: "text-center",
          right: "text-end",
        }[column.style.align];
      }
      return ["number", "currency", "percent"].includes(column.format)
        ? "text-end"
        : "";
    },
    styleClasses(style, value) {
      if (!style) return [];
      const number = Number(value);
      let color = style.color;
      if (style.highlight === "negative_red" && number < 0) color = "negative";
      if (style.highlight === "positive_green" && number > 0)
        color = "positive";
      return [
        style.bold ? "cb-bold" : "",
        style.italic ? "cb-italic" : "",
        style.size && style.size !== "normal" ? `cb-size-${style.size}` : "",
        color && color !== "default" ? `cb-color-${color}` : "",
      ];
    },
    format(value, type) {
      if (value === null || value === undefined || value === "") return "–";
      const locale = this.$i18n.locale;
      if (type === "currency") {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: this.currency || "HUF",
          maximumFractionDigits: 0,
        }).format(value);
      }
      if (type === "number") {
        return new Intl.NumberFormat(locale, {
          maximumFractionDigits: 2,
        }).format(value);
      }
      if (type === "percent") {
        return new Intl.NumberFormat(locale, {
          style: "percent",
          maximumFractionDigits: 1,
        }).format(value);
      }
      if (type === "date") {
        return new Date(value).toLocaleDateString(locale);
      }
      return String(value);
    },
    chartComponent(kind) {
      return {
        line: "LineChart",
        bar: "BarChart",
        pie: "PieChart",
        doughnut: "DoughnutChart",
      }[kind];
    },
    chartData(widget) {
      const rows = this.rowsOf(widget);
      const round = ["pie", "doughnut"].includes(widget.kind);
      const fill = widget.kind === "line" && widget.y_fields.length === 1;
      const labels = [...new Set(rows.map((row) => row[widget.x_field]))];

      let sets = widget.y_fields.map((y) => ({
        label: y.label || y.field,
        data: rows.map((row) => Number(row[y.field])),
      }));
      if (widget.series) {
        const field = widget.y_fields[0].field;
        sets = [...new Set(rows.map((row) => row[widget.series]))].map(
          (name) => ({
            label: String(name),
            data: labels.map((label) => {
              const row = rows.find(
                (r) => r[widget.x_field] === label && r[widget.series] === name
              );
              return row ? Number(row[field]) : 0;
            }),
          })
        );
      }

      return {
        labels: labels.map((label) => String(label)),
        datasets: sets.map((set, i) => ({
          ...set,
          borderColor: round ? "#fff" : PALETTE[i % PALETTE.length],
          backgroundColor: round
            ? labels.map((_, n) => PALETTE[n % PALETTE.length])
            : fill
            ? "rgba(113,58,232,0.18)"
            : PALETTE[i % PALETTE.length],
          fill,
          lineTension: 0.4,
        })),
      };
    },
    chartOptions(widget) {
      const round = ["pie", "doughnut"].includes(widget.kind);
      const many = widget.series || widget.y_fields.length > 1 || round;
      return {
        maintainAspectRatio: false,
        plugins: { legend: { display: !!many } },
        scales: round
          ? {}
          : { x: { grid: { display: false } }, y: { beginAtZero: true } },
      };
    },
  },
};
</script>

<style scoped>
.custom-block {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem;
}
:global(.editor-mode) .custom-block {
  position: static;
  z-index: auto;
  padding-top: 2.25rem;
}
.custom-block-select {
  min-width: 12rem;
}
.custom-block-controls {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}
.custom-block-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--pf-muted, #6b6880);
}
.custom-block-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-auto-rows: 3rem;
  gap: 0.75rem;
  transition: opacity 0.15s;
}
.custom-block-grid.loading {
  opacity: 0.55;
}
.custom-block-widget {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 0.5rem 0.75rem;
  overflow: hidden;
  border: 1px solid var(--pf-border, #e4e1f0);
  border-radius: 0.625rem;
}
.custom-block-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--pf-muted, #6b6880);
}
.custom-block-kpi {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}
.custom-block-chart {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.custom-block-table {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
.cb-bold {
  font-weight: 800;
}
.cb-italic {
  font-style: italic;
}
.cb-size-small {
  font-size: 0.8125rem;
}
.cb-size-large {
  font-size: 1.125rem;
}
.cb-color-accent {
  color: var(--pf-accent, #713ae8);
}
.cb-color-positive {
  color: #1c8c5e;
}
.cb-color-negative {
  color: #c62f3e;
}
.cb-color-muted {
  color: var(--pf-muted, #6b6880);
}
@media (max-width: 768px) {
  .custom-block-widget {
    grid-column: 1 / -1 !important;
    grid-row: auto !important;
    min-height: 12rem;
  }
}
</style>
